import React from 'react';
import type { ReactNode } from 'react';
import { StyleSheet } from 'react-native';
import { Pressable } from 'react-native-gesture-handler';

import { InfoIconCircle } from '@/assets/icons/info-circle';
import { PromoIcon } from '@/assets/icons/promoIcon';
import { PromoTag, PromoTagLabel } from '@/components/PromoTag';
import { Text } from '@/components/TextComponent';
import { Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { common, designSystem, withOpacity } from '@/styles/styles';
import { isAndroid } from '@/utils/constants-platform-specific';

import { SafetyShadow } from './SafetyShadow';
import { useIsTextClipped } from './use-is-text-clipped';

export type EntryCardPromoProps = {
    label: string;
    overflow?: { label: string; icon?: ReactNode };
    onInfoPress?: () => void;
};

export const EntryCardPromoTag = ({ label, overflow, onInfoPress }: EntryCardPromoProps) => {
    const { isClipped, onTextLayout } = useIsTextClipped();

    return (
        // Hug the content and cap at the card width: a name-only tag stays short, a long label
        // grows until it clips and fades under the safety shadow.
        <PromoTag backgroundColor={ds.colorToken.background.neutral.subtle}>
            <Row flexShrink={1} alignItems="center" gap="s8">
                <PromoIcon size={16} />
                <Row flexShrink={isAndroid ? 0 : 1} overflow="hidden">
                    <PromoTagLabel
                        color={ds.colorToken.text.primary}
                        numberOfLines={1}
                        ellipsizeMode="clip"
                        flexShrink={1}
                    >
                        {label}
                    </PromoTagLabel>
                </Row>
                {/* Invisible, width-constrained copy: wraps to >1 line exactly when the label clips. */}
                <PromoTagLabel
                    onTextLayout={onTextLayout}
                    style={[common.measureLayer, styles.measureOffset]}
                    accessibilityElementsHidden
                    importantForAccessibility="no-hide-descendants"
                >
                    {label}
                </PromoTagLabel>
                {overflow && isClipped ? (
                    <SafetyShadow stops={PROMO_SHADOW_STOPS} style={styles.shadow} width={SHADOW_WIDTH} />
                ) : null}
            </Row>
            {/* Content Trailing (pinned, never shrinks): the optional bonus then the optional info
                icon. When a bonus is present the safety shadow dissolves the clipped label into it. */}
            {overflow || onInfoPress ? (
                <Row flexShrink={0} alignItems="center" gap="s8">
                    {overflow ? (
                        <Row alignItems="center" gap="s4">
                            <Text variant="labelLarge2" lineHeight={16} letterSpacing={-0.08} color="neutral300">
                                •
                            </Text>
                            {overflow.icon}
                            <PromoTagLabel color={ds.colorToken.text.brand} numberOfLines={1}>
                                {overflow.label}
                            </PromoTagLabel>
                        </Row>
                    ) : null}
                    {onInfoPress ? (
                        <Pressable
                            onPress={onInfoPress}
                            hitSlop={8}
                            testID="entry-card-promo-info"
                            disabled={!onInfoPress}
                        >
                            <InfoIconCircle width={16} height={16} />
                        </Pressable>
                    ) : null}
                </Row>
            ) : null}
        </PromoTag>
    );
};

const PROMO_SHADOW_STOPS = [
    { color: withOpacity(designSystem.colors.neutral600, 0), position: 0 },
    { color: designSystem.colors.neutral600, position: 1 },
];

const PROMO_ICON_AREA = 24; // PromoIcon(16) + gap(s8)
const SHADOW_WIDTH = 24;

const styles = StyleSheet.create({
    measureOffset: { left: PROMO_ICON_AREA },
    shadow: { position: 'absolute', right: 0, top: 0, bottom: 0, width: SHADOW_WIDTH },
});
